<template>
  <v-dialog v-model="show" width="600" persistent eager>
    <v-form autocomplete="off" @submit.prevent="submit">
      <v-card class="mx-auto" outlined>
        <v-card-title class="headline">
          Import Recipes from Google Sheet
        </v-card-title>

        <v-divider />

        <v-card-text class="py-5">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="form.google_sheet_id"
                label="Google Sheet ID or URL"
                placeholder="Enter Google Sheet ID or full URL"
                :error-messages="form.$getError('google_sheet_id')"
                :hide-details="!form.$getError('google_sheet_id')"
                outlined
                flat
              />
              <div class="text-caption grey--text mt-2">
                You can paste the full Google Sheet URL or just the sheet ID
              </div>
            </v-col>

            <v-col cols="12">
              <div class="text-subtitle-2 grey--text text--darken-2 mb-2">
                Add to categories
              </div>
              <v-autocomplete
                v-model="form.category_ids"
                :items="categories"
                :loading="loadingCategories"
                item-text="name"
                item-value="id"
                label="Select recipe categories"
                placeholder="Select one or more categories"
                :error-messages="form.$getError('category_ids')"
                :hide-details="!form.$getError('category_ids')"
                multiple
                chips
                deletable-chips
                small-chips
                clearable
                outlined
                flat
              />
              <div class="text-caption grey--text mt-1">
                Applied on top of the sheet's Category column
              </div>
            </v-col>

            <v-col cols="12" class="pt-0">
              <v-checkbox
                v-model="form.use_category_column"
                label="Use Category Column in Recipe template"
                hide-details
                class="mt-0"
              />
              <div class="text-caption grey--text mt-1">
                When off, the sheet's Category column is ignored and only the
                categories selected above are applied
              </div>
            </v-col>

            <v-col cols="12" class="pt-0">
              <div class="text-subtitle-2 grey--text text--darken-2 mb-2">
                Re-import Options
              </div>
              <v-checkbox
                v-model="form.reset_ingredients"
                label="Reset ingredients of existing recipes"
                hide-details
                class="mt-0"
              />
              <v-checkbox
                v-model="form.reset_booster_directions"
                label="Reset flavour boosters &amp; directions of existing recipes"
                hide-details
                class="mt-0"
              />
              <div class="text-caption grey--text mt-2">
                <template v-if="isOverwriteMode">
                  Existing recipes found in the sheet will be wiped and rebuilt
                  from the sheet, including their dietary restriction tags.
                </template>
                <template v-else>
                  Existing recipes (matched by name) will be skipped — only new
                  recipes are imported.
                </template>
              </div>
              <div
                v-if="form.reset_ingredients && !form.reset_booster_directions"
                class="text-caption warning--text mt-1"
              >
                Note: with only ingredients reset, changed direction text will
                be appended to the existing directions instead of replacing
                them.
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-6 pb-6 pt-5">
          <v-spacer />

          <v-btn
            class="px-12 error--text"
            text
            @click="cancel"
            :disabled="form.$busy"
          >
            Cancel
          </v-btn>

          <v-btn
            class="primary px-12"
            text
            type="submit"
            :loading="form.$busy"
            :disabled="form.$busy"
          >
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import Form from '@/utils/form'

export default {
  name: 'ImportRecipeDialog',

  data() {
    return {
      show: false,
      categories: [],
      loadingCategories: false,
      form: new Form({
        google_sheet_id: '',
        category_ids: [],
        use_category_column: true,
        reset_ingredients: false,
        reset_booster_directions: false,
      }),
    }
  },

  computed: {
    isOverwriteMode() {
      return this.form.reset_ingredients || this.form.reset_booster_directions
    },
  },

  methods: {
    open() {
      this.show = true
      this.form.$reset()
      this.fetchCategories()
    },

    async fetchCategories() {
      this.loadingCategories = true
      try {
        const categories = await this.$store.dispatch(
          'recipe/getRecipeCategories'
        )
        this.categories = categories.filter((category) => category.id > 0)
      } catch (error) {
        this.categories = []
      } finally {
        this.loadingCategories = false
      }
    },

    close() {
      this.show = false
    },

    submit() {
      this.$emit('submit', this.form)
    },

    cancel() {
      this.form.$reset()
      this.close()
    },
  },
}
</script>
